import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
  type KeyboardEvent,
  type MouseEvent,
  type MutableRefObject,
} from 'react';

const ACCEPT =
  'image/jpeg,image/png,image/tiff,image/webp,image/avif,image/heic,image/heif,' +
  '.jpg,.jpeg,.png,.tif,.tiff,.webp,.avif,.heic,.heif';

interface DropzoneProps {
  variant?: 'desktop' | 'mobile' | 'compact';
  onFiles: (files: File[]) => void;
  /** Order size cap for the caption copy, e.g. "5 GB". */
  capLabel?: string;
  /** When true the order is at the size cap — the zone shows a full/disabled state. */
  full?: boolean;
  /** The parent's handle onto the picker (stepper "Upload" click — Clarity). */
  pickRef?: MutableRefObject<(() => void) | null>;
}

/**
 * Upload dropzone: click-to-browse + drag-and-drop, with a gold drag-over state.
 *  - `desktop`  — the tall empty-state hero shown before any photos exist.
 *  - `compact`  — a slim "add more" bar pinned above the grid once photos exist.
 *  - `mobile`   — the mobile empty-state tap target.
 * When `full`, the zone is disabled (the 5 GB order cap is handled upstream).
 */
export function Dropzone({
  variant = 'desktop',
  onFiles,
  // Fallback only: every call site passes the live label from
  // gbCapLabel(limits.maxOrderBytes). Kept in step with the seed so a future
  // caller that forgets cannot silently show a stale cap.
  capLabel = '10 GB',
  full = false,
  pickRef,
}: DropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const dragDepth = useRef(0);
  const [over, setOver] = useState(false);
  const mobile = variant === 'mobile';
  const compact = variant === 'compact';

  function pick() {
    if (full) return;
    inputRef.current?.click();
  }
  // Register the picker with the parent (the stepper's "Upload" step opens it).
  const pickFnRef = useRef(pick);
  pickFnRef.current = pick;
  useEffect(() => {
    if (!pickRef) return;
    pickRef.current = () => pickFnRef.current();
    return () => {
      pickRef.current = null;
    };
  }, [pickRef]);
  /** Whole-zone click → picker. The programmatic input.click() bubbles back
   *  here — ignore it so the picker never double-opens. */
  function onZoneClick(e: MouseEvent<HTMLDivElement>) {
    if (e.target === inputRef.current) return;
    pick();
  }
  function onInput(e: ChangeEvent<HTMLInputElement>) {
    const files = e.target.files ? Array.from(e.target.files) : [];
    if (files.length) onFiles(files);
    e.target.value = ''; // allow re-adding the same file
  }
  function onKeyDown(e: KeyboardEvent) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      pick();
    }
  }
  function onDragEnter(e: DragEvent) {
    e.preventDefault();
    if (full) return;
    dragDepth.current += 1;
    setOver(true);
  }
  function onDragOver(e: DragEvent) {
    e.preventDefault();
  }
  function onDragLeave(e: DragEvent) {
    e.preventDefault();
    dragDepth.current -= 1;
    if (dragDepth.current <= 0) {
      dragDepth.current = 0;
      setOver(false);
    }
  }
  function onDrop(e: DragEvent) {
    e.preventDefault();
    dragDepth.current = 0;
    setOver(false);
    const files = e.dataTransfer?.files ? Array.from(e.dataTransfer.files) : [];
    if (files.length) onFiles(files);
  }

  const className =
    `dropzone${mobile ? ' dropzone--mobile' : ''}${compact || mobile ? ' dropzone--compact' : ''}` +
    `${over ? ' dropzone--over' : ''}${full ? ' dropzone--full' : ''}`;

  // Round 9: mobile renders the same slim bar as "add more" (a big drop box
  // wastes screen on a device with no drag-and-drop) + the formats/limits
  // caption line (must stay visible — accessibility round).
  if (compact || mobile) {
    const label = full
      ? `Order size limit reached (${capLabel})`
      : mobile
        ? 'Tap to add photos'
        : 'Add more photos';
    return (
      <div
        className={className}
        role="button"
        tabIndex={0}
        aria-label={label}
        aria-disabled={full || undefined}
        onClick={onZoneClick}
        onKeyDown={onKeyDown}
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        <span className="dropzone__plus" aria-hidden="true">
          +
        </span>
        <span className="dropzone__addlabel">{label}</span>
        {!full && !mobile && <span className="dropzone__addhint">or drag them here</span>}
        {!full && mobile && (
          <span className="dropzone__caption dropzone__caption--bar">
            JPEG · PNG · TIFF · HEIC · WEBP · up to {capLabel} per order · sRGB recommended
          </span>
        )}
        <input
          ref={inputRef}
          className="dropzone__input"
          type="file"
          accept={ACCEPT}
          multiple
          onChange={onInput}
        />
      </div>
    );
  }

  return (
    <div
      className={className}
      role="button"
      tabIndex={0}
      aria-label="Add photos"
      aria-disabled={full || undefined}
      onClick={onZoneClick}
      onKeyDown={onKeyDown}
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <div className="dropzone__icon" aria-hidden="true">
        ↑
      </div>
      {mobile ? (
        <div className="dropzone__title">
          {full ? "That's a full order" : 'Tap to add from camera roll'}
        </div>
      ) : (
        <div className="dropzone__title">
          {full ? (
            "That's a full order"
          ) : (
            <>
              Drag photos here, or <span className="dropzone__browse">browse files</span>
            </>
          )}
        </div>
      )}
      <div className="dropzone__caption">
        {full
          ? `You've reached the ${capLabel} limit for one order. Start a second order for more.`
          : `JPEG · PNG · TIFF · HEIC · WEBP · up to ${capLabel} per order. sRGB recommended.`}
      </div>
      <input
        ref={inputRef}
        className="dropzone__input"
        type="file"
        accept={ACCEPT}
        multiple
        onChange={onInput}
      />
    </div>
  );
}
